<template>
<div v-if="hasAutoprovisioningPermission">
  <h3>Autoprovisioning Configuration</h3>
      <hr />
      <div v-if="loading" class="alert alert-info">
        <p>Loading autoprovisioning configuration...</p>
      </div>
      <div v-else-if="showForm">
        <AutoprovisioningForm 
          :domain-uuid="domainUuid" 
          :domain-name="domainName"
          :config="formMode === 'edit' ? config : null"
          @configCreated="handleConfigCreated" 
          @configUpdated="handleConfigUpdated" 
          @cancel="showForm = false"
        />
      </div>
      <div v-else-if="config">
  
        <div class="well">
          <div class="row">
            <div class="col-sm-12">
              <h4>Current Configuration</h4>
  
              <div class="form-group">
                <p class="control-label">Roster Service</p>
                <p>{{ formatRosterService(rosterService) }}</p>
              </div>

              <div class="form-group">
                <p class="control-label" 
                  role="button" 
                  @click="toggleConfigDetails"
                  :aria-label="showConfigDetails ? 'Hide configuration details' : 'Show configuration details'"
                  :aria-expanded="showConfigDetails">
                  <i class="fa" :class="showConfigDetails ? 'fa-caret-down' : 'fa-caret-right'"></i>
                  Configuration
                </p>
                <AutoprovisioningConfigDisplay v-if="showConfigDetails" :config="config" />
              </div>
              <div class="form-group">
                <p class="control-label">Import Options</p>
                <p>Auto-commit import: <strong>{{ boolPropDisplay(autoCommitImport) }}</strong></p>
                <p>Auto-deactivate users: <strong>{{ boolPropDisplay(autoDeactivateUsers) }}</strong></p>
                <p>Auto-update titles: <strong>{{ boolPropDisplay(autoupdateTitles) }}</strong></p>
                <p>Two Kit Import: <strong>{{ boolPropDisplay(twoKit) }}</strong></p>
              </div>
              <div class="form-group">
                <p class="control-label">CSV S3 Output Directory</p>
                <p><code>{{ csvUploadS3Prefix }}</code></p>
              </div>
              <div class="form-group" v-if="config.updated_at">
                <p class="control-label">Last Updated</p>
                <p>{{ new Date(config.updated_at).toLocaleString() }}</p>
              </div>
              <div class="form-group">
                <button class="btn btn-success" @click="showImportOptions">
                  <i class="fa fa-play"></i> On-demand import
                </button>
                <button class="btn btn-primary" @click="editConfig">Edit Configuration</button>
                <button class="btn btn-danger" @click="confirmDelete">Delete Configuration</button>
              </div>
        
              <!-- Schedule component -->
              <AutoprovisioningSchedule 
                v-if="config"
                :domain-uuid="domainUuid"
                :domain-name="domainName"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="alert alert-info">
          <p>No automated user provisioning configuration exists for this domain.</p>
        </div>
        <button class="btn btn-primary" @click="setupConfig">Set up Autoprovisioning</button>
      </div>
      
      <!-- Confirmation Modal -->
      <modal :value="showDeleteModal" title="Confirm Delete" @hide="showDeleteModal = false" @input="showDeleteModal = $event">
        <p>Are you sure you want to delete this autoprovisioning configuration?</p>
        <div slot="footer">
          <button type="button" class="btn btn-default" @click="showDeleteModal = false">Cancel</button>
          <button type="button" class="btn btn-danger" @click="deleteConfig">Delete</button>
        </div>
      </modal>

      <!-- On-demand Import Modal -->
      <import-modal 
        v-if="config"
        :external-open.sync="showImportModal" 
        :domain-uuid="domainUuid"
        :config="config"
        @success="handleImportSuccess"
        @error="handleImportError"
        @cancel="showImportModal = false"
      ></import-modal>
      <br/>
      <hr/>
    </div>
  </template>
  
  <script>
  import { Permissions } from '@/client_auth/permissions'
  import AutoprovisioningForm from './AutoprovisioningForm.vue'
  import AutoprovisioningSchedule from './AutoprovisioningSchedule.vue'
  import AutoprovisioningConfigDisplay from './AutoprovisioningConfigDisplay.vue'
  import ImportModal from './ImportModal.vue'
  import { Modal } from 'uiv'
  import { ROSTER_SERVICE_CLEVER, ROSTER_SERVICE_ONEROSTER, ROSTER_SERVICE_S3CSV, ROSTER_SERVICE_NAMES } from './constants'
  
  export default {
    name: 'Autoprovisioning',
    components: {
      AutoprovisioningForm,
      AutoprovisioningSchedule,
      AutoprovisioningConfigDisplay,
      ImportModal,
      Modal
    },
    props: {
      domainUuid: {
        type: String,
        required: true
      },
      domainName: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        ROSTER_SERVICE_CLEVER,
        ROSTER_SERVICE_ONEROSTER,
        ROSTER_SERVICE_S3CSV,
        loading: false,
        config: null,
        error: null,
        hasAutoprovisioningPermission: false,
        showForm: false,
        showDeleteModal: false,
        showImportModal: false,
        showConfigDetails: false,
        formMode: null // 'create' or 'edit'
      }
    },
    computed: {
      rosterService() {
        return this.config?.lambda_input?.roster_service || '';
      },
      autoCommitImport() {
        return this.config?.lambda_input?.import_options?.autocommit !== false;
      },
      autoDeactivateUsers() {
        return !!this.config?.lambda_input?.import_options?.deactivate_missing;
      },
      autoupdateTitles() {
        return this.config?.lambda_input?.import_options?.autoupdate_titles !== false;
      },
      twoKit() {
        return !!this.config?.lambda_input?.import_options?.two_kit;
      },
      csvUploadS3Prefix() {
        return this.config?.lambda_input?.csv_upload_s3_prefix || '';
      }
    },
    methods: {
      fetchConfig() {
        this.loading = true
        this.$angularWorld.adminApi.getAutoprovisioningConfig(this.domainUuid)
          .then(config => {
            console.log('Fetched config:', config)
            this.config = config
            this.loading = false
          })
          .catch(error => {
            console.error('Error fetching autoprovisioning config:', error)
            this.error = error
            this.loading = false
          })
      },
      setupConfig() {
        this.formMode = 'create'
        this.showForm = true
      },
      editConfig() {
        this.formMode = 'edit'
        this.showForm = true
      },
      confirmDelete() {
        // Show confirmation modal
        this.showDeleteModal = true
      },
      boolPropDisplay(property) {
        return property ? 'Yes' : 'No'
      },
      deleteConfig() {
        this.loading = true
        this.$angularWorld.adminApi.deleteAutoprovisioningConfig(this.domainUuid)
          .then(() => {
            this.showDeleteModal = false
            this.config = null
            this.loading = false
          })
          .catch(error => {
            console.error('Error deleting autoprovisioning config:', error)
            this.error = error
            this.loading = false
            this.showDeleteModal = false
          })
      },
      showImportOptions() {
        this.showImportModal = true
      },
      toggleConfigDetails() {
        this.showConfigDetails = !this.showConfigDetails
      },
      handleImportSuccess(importUrl) {
        console.log('Import initiated successfully:', importUrl)
        if (importUrl) {
          setTimeout(() => {
            window.open(importUrl, '_blank').focus()
          }, 1500)
        }
      },
      handleImportError(error) {
        console.error('Error initiating import:', error)
      },
      handleConfigCreated(config) {
        console.log('Config created:', config)
        this.config = config
        this.showForm = false
        this.formMode = null
        // Refetch the config to ensure we have the latest data
        this.fetchConfig()
      },
      handleConfigUpdated(config) {
        console.log('Config updated:', config)
        this.config = config
        this.showForm = false
        this.formMode = null
        // Refetch the config to ensure we have the latest data
        this.fetchConfig()
      },
      formatRosterService(service) {
        if (!service) return 'N/A'
        return ROSTER_SERVICE_NAMES[service] || service
      }
    },
    mounted() {
      this.$angularWorld.auth.doesUserHavePermission([Permissions.UserOps]).then(hasPermission => {
        this.hasAutoprovisioningPermission = hasPermission;
        if (this.hasAutoprovisioningPermission) {
          this.fetchConfig();
        }
      });
    }
  }
  </script>
  
  <style scoped>
  .control-label {
    font-weight: bold;
    margin-bottom: 0;
  }
  </style>